import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import ProfileTagsCollapse from '../../components/ProfileTagsCollapse/ProfileTagsCollapse';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FRONT_DEFAULT_AVATAR, resolveAvatarUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';

const popularCities = [
  "Москва", "Санкт-Петербург", "Новосибирск", "Екатеринбург", "Казань", 
  "Нижний Новгород", "Челябинск", "Самара", "Омск", "Ростов-на-Дону", 
  "Уфа", "Красноярск", "Воронеж", "Пермь", "Волгоград"
];

const AutoWidthInput = ({ value, onChange, placeholder, className, style }) => {
  return (
    <div className={className} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', background: 'transparent', border: 'none', ...style }}>
      <span style={{ 
        visibility: 'hidden', 
        whiteSpace: 'pre',
        font: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
        fontFamily: 'inherit'
      }}>
        {value || placeholder}
      </span>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          padding: 0,
          margin: 0,
          font: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
          letterSpacing: 'inherit',
          fontFamily: 'inherit',
          color: 'inherit'
        }}
      />
    </div>
  );
};

function EditProfile() {
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  
  const normalizeNicknameBody = (value) =>
    (value || '')
      .trim()
      .replace(/^@+/, '')
      .replace(/[^a-zA-Z0-9_.-]/g, '');

  const [avatarUrl, setAvatarUrl] = useState(FRONT_DEFAULT_AVATAR);
  const [currentPhotoId, setCurrentPhotoId] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [onboarding, setOnboarding] = useState(null);

  // State for form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [city, setCity] = useState("");
  const [profession, setProfession] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [bio, setBio] = useState("");
  
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isBioOverflowing, setIsBioOverflowing] = useState(false);
  const bioRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bioRef.current) {
        const isMobile = window.innerWidth <= 767.84;
        const lineHeight = isMobile ? (window.innerWidth * 15.5 / 360) : (Math.min(window.innerWidth, 2560) * 30 / 1920);
        const maxHeight = lineHeight * 7;
        if (bioRef.current.scrollHeight > maxHeight + 5) {
          setIsBioOverflowing(true);
        } else {
          setIsBioOverflowing(false);
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [bio]);
  
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [remainingCount, setRemainingCount] = useState(0);
  const containerRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState('100%');

  // Fetch cities from API with debounce
  useEffect(() => {
    if (!city || city.length < 2) {
      setCitySuggestions([]);
      return;
    }
    
    const fetchCities = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/Profile/cities?q=${encodeURIComponent(city)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setCitySuggestions(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      } catch (e) {
        console.error('Failed to fetch cities', e);
      }
    };

    const timer = setTimeout(fetchCities, 300);
    return () => clearTimeout(timer);
  }, [city]);

  // Load profile from server
  useEffect(() => {
    let isActive = true;

    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (isActive) setLoadingProfile(false);
        return;
      }
      try {
        const resp = await fetch(`${API_BASE}/api/Profile/me`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (data && isActive) {
          setFirstName(data.firstName || "");
          setLastName(data.lastName || "");
          
          const uname = normalizeNicknameBody(data.username || data.nickname || "");
          setUsername(uname);

          setEmail(data.email || "");
          setPhoneNumber(data.phoneNumber || "");
          setCity(data.city || "");
          setProfession(data.profession || data.jobTitle || "");
          
          if (data.birthDate) {
            const dateObj = new Date(data.birthDate);
            setBirthDate(dateObj.toLocaleDateString('ru-RU')); 
          } else {
            setBirthDate("");
          }

          setBio(data.description || data.bio || "");
          setAvatarUrl(data.avatarUrl ? resolveAvatarUrl(data.avatarUrl) : FRONT_DEFAULT_AVATAR);
          
          if (data.photos && Array.isArray(data.photos)) {
            const mapped = data.photos.map(p => {
              if (typeof p === 'string') return { id: null, url: resolveAvatarUrl(p), isPrimary: false };
              return { ...p, url: resolveAvatarUrl(p.url || p.Url) };
            });
            setGalleryPhotos(mapped);
            
            const current = mapped.find(p => p.isPrimary || p.url === resolveAvatarUrl(data.avatarUrl));
            setCurrentPhotoId(current ? (current.id || current.Id) : null);
          } else {
            setGalleryPhotos([]);
            setCurrentPhotoId(null);
          }
          setOnboarding(data.onboarding || null);
        }
      } catch (e) {
        console.error('Failed to load profile in EditProfile:', e);
      } finally {
        if (isActive) {
          setLoadingProfile(false);
        }
      }
    };
    fetchProfile();
    return () => {
      isActive = false;
    };
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const nicknameBody = normalizeNicknameBody(username);
    if (!nicknameBody) {
      alert('Никнейм обязателен');
      return;
    }
    const payload = {
      FirstName: firstName,
      LastName: lastName,
      Nickname: `@${nicknameBody}`,
      Bio: bio,
      JobTitle: profession,
      City: city,
      PhoneNumber: phoneNumber
    };

    if (birthDate) {
      const parts = birthDate.split('.');
      if (parts.length === 3) {
        const d = parts[0].trim().padStart(2, '0');
        const m = parts[1].trim().padStart(2, '0');
        const y = parts[2].trim();
        if (!isNaN(parseInt(d)) && !isNaN(parseInt(m)) && !isNaN(parseInt(y))) {
          payload.BirthDate = `${y}-${m}-${d}`;
        }
      } else {
        const d = new Date(birthDate);
        if (!isNaN(d.getTime())) {
          payload.BirthDate = d.toISOString().split('T')[0];
        }
      }
    }

    if (!token) {
      localStorage.setItem('firstName', firstName);
      localStorage.setItem('lastName', lastName);
      localStorage.setItem('username', nicknameBody);
      localStorage.setItem('email', email);
      localStorage.setItem('phoneNumber', phoneNumber);
      localStorage.setItem('city', city);
      localStorage.setItem('profession', profession);
      localStorage.setItem('birthDate', birthDate);
      localStorage.setItem('bio', bio);
      navigate('/profile');
      return;
    }

    try {
      if (currentPhotoId) {
        await fetch(`${API_BASE}/api/Profile/avatars/${currentPhotoId}/primary`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(err => console.error("Failed to set primary avatar", err));
      }

      const res = await fetch(`${API_BASE}/api/Profile/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const t = await res.text();
        alert('Ошибка при сохранении: ' + (t || res.statusText));
        return;
      }
      navigate('/profile');
    } catch (e) {
      console.error(e);
      alert('Не удалось сохранить профиль: ' + e.message);
    }
  };

  const compressImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          const webpName = file.name.replace(/\.[^/.]+$/, "") + ".webp";
          resolve(new File([blob], webpName, { type: 'image/webp', lastModified: Date.now() }));
        }, 'image/webp', 0.85);
        
        URL.revokeObjectURL(img.src);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(img.src);
        reject(e);
      };
    });
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleGalleryFileSelect = () => {
    if (galleryInputRef.current) galleryInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) { alert('Нужна авторизация'); return; }

    const fd = new FormData();
    
    try {
      setIsUploading(true);
      
      const compressedFiles = await Promise.all(
        Array.from(files).map(async f => {
          if (f.type.startsWith('image/')) {
            try {
              return await compressImage(f, 1920, 1920);
            } catch (err) {
              console.error('Failed to compress image:', err);
              return f; // Fallback to original
            }
          }
          return f;
        })
      );
      
      compressedFiles.forEach(f => fd.append('files', f));
      const resp = await fetch(`${API_BASE}/api/Profile/avatars?makePrimary=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.avatarUrl) {
          setAvatarUrl(resolveAvatarUrl(data.avatarUrl));
          // Обновляем объект пользователя в localStorage, чтобы в чатах отображалась новая аватарка
          const userObj = JSON.parse(localStorage.getItem('user') || '{}');
          userObj.avatarUrl = data.avatarUrl;
          localStorage.setItem('user', JSON.stringify(userObj));
        }
        
        if (data.uploaded && data.uploaded.length > 0) {
          const lastUploaded = data.uploaded[data.uploaded.length - 1];
          const newId = lastUploaded.id || lastUploaded.Id;
          
          setCurrentPhotoId(newId);

          const newPhotos = data.uploaded.map(u => ({ 
            id: u.id || u.Id, 
            url: resolveAvatarUrl(u.url || u.Url), 
            isPrimary: (u.id || u.Id) === newId 
          }));

          setGalleryPhotos(prev => {
            const oldPhotos = prev.map(p => ({ ...p, isPrimary: false }));
            return [...newPhotos, ...oldPhotos];
          });
        }
        setShowPhotoMenu(false);
      } else {
        const errorText = await resp.text();
        alert("Ошибка при загрузке фото: " + (errorText || resp.statusText));
      }
    } catch (e) {
      console.error(e);
      alert("Ошибка сети при смене фото");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploading(false);
    }
  };

  const handleGalleryFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) { alert('Нужна авторизация'); return; }

    const fd = new FormData();
    
    try {
      setIsUploading(true);
      
      const compressedFiles = await Promise.all(
        Array.from(files).map(async f => {
          if (f.type.startsWith('image/')) {
            try {
              return await compressImage(f, 1920, 1920);
            } catch (err) {
              console.error('Failed to compress image:', err);
              return f; // Fallback to original
            }
          }
          return f;
        })
      );
      
      compressedFiles.forEach(f => fd.append('files', f));
      const resp = await fetch(`${API_BASE}/api/Profile/avatars?makePrimary=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.uploaded && data.uploaded.length > 0) {
          const newPhotos = data.uploaded.map(u => ({ 
            id: u.id || u.Id, 
            url: resolveAvatarUrl(u.url || u.Url), 
            isPrimary: false 
          }));
          setGalleryPhotos(prev => [...newPhotos, ...prev]);
        } 
        setShowPhotoMenu(false);
      } else {
        const errorText = await resp.text();
        alert("Ошибка при загрузке фото: " + (errorText || resp.statusText));
      }
    } catch (e) {
      console.error(e);
      alert("Ошибка сети");
    } finally {
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      setIsUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!window.confirm("Удалить фото?")) return;

    if (currentPhotoId) {
      try {
        const resp = await fetch(`${API_BASE}/api/Profile/avatars/${currentPhotoId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          setGalleryPhotos(prev => prev.filter(p => p.id !== currentPhotoId));
          setAvatarUrl(FRONT_DEFAULT_AVATAR);
          setCurrentPhotoId(null);
          setShowPhotoMenu(false);
        }
      } catch(e) { console.error(e); }
      return;
    }

    try {
      const resp = await fetch(`${API_BASE}/api/Profile/avatar`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setAvatarUrl(data.avatarUrl ? resolveAvatarUrl(data.avatarUrl) : FRONT_DEFAULT_AVATAR);
        setShowPhotoMenu(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteSpecificPhoto = async (photoId, e) => {
    e.stopPropagation();
    if (!window.confirm("Удалить это фото?")) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const resp = await fetch(`${API_BASE}/api/Profile/avatars/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setGalleryPhotos(prev => prev.filter(p => p.id !== photoId));
        if (photoId === currentPhotoId) {
          setAvatarUrl(FRONT_DEFAULT_AVATAR);
          setCurrentPhotoId(null);
        }
      }
    } catch(e) { console.error(e); }
  };

  const displayPhotos = (() => {
    return [...galleryPhotos];
  })();

  // Dynamically measure how many items fit inside a single row of the photos container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      if (isExpanded) {
        setVisibleCount(displayPhotos.length);
        setRemainingCount(0);
        setHeaderWidth('100%');
        return;
      }

      // Temporarily set flex-wrap to nowrap and display all elements to measure natural widths
      container.style.flexWrap = 'nowrap';
      
      const children = Array.from(container.children);
      const hadHiddenClass = children.map(c => c.classList.contains('profile-photos__item--hidden'));

      children.forEach(c => {
        c.classList.remove('profile-photos__item--hidden');
        c.style.display = 'flex';
      });

      const widths = children.map(child => child.getBoundingClientRect().width);

      // Restore direct DOM override so React's virtual DOM styles take full control!
      children.forEach((c, i) => {
        c.style.removeProperty('display');
        if (hadHiddenClass[i]) {
          c.classList.add('profile-photos__item--hidden');
        }
      });
      container.style.removeProperty('flex-wrap');

      const containerWidth = container.offsetWidth;
      const gap = parseFloat(getComputedStyle(container).gap) || 10;

      let totalWidth = 0;
      let fitCount = 0;

      for (let i = 0; i < widths.length; i++) {
        const itemWidth = widths[i] || (containerWidth * 140 / 1920);
        const nextWidth = totalWidth + itemWidth + (i > 0 ? gap : 0);
        if (nextWidth <= containerWidth) {
          totalWidth = nextWidth;
          fitCount++;
        } else {
          break;
        }
      }

      // Safe check: show at least 1 photo if some exist
      if (fitCount === 0 && widths.length > 0) {
        fitCount = 1;
      }

      if (fitCount < displayPhotos.length) {
        setVisibleCount(fitCount);
        setRemainingCount(displayPhotos.length - fitCount + 1);
      } else {
        setVisibleCount(displayPhotos.length);
        setRemainingCount(0);
      }

      // Align header's right edge (upload button right edge) with the right edge of the blurred last photo on desktop
      if (window.innerWidth > 768 && totalWidth > 0 && totalWidth < containerWidth) {
        setHeaderWidth(`${totalWidth}px`);
      } else {
        setHeaderWidth('100%');
      }
    };

    // Run measurement inside requestAnimationFrame to ensure DOM is ready
    const handle = requestAnimationFrame(measure);

    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(handle);
      window.removeEventListener('resize', measure);
    };
  }, [galleryPhotos, isExpanded, displayPhotos]);

  const rawInterests = (onboarding && onboarding.flatMap) 
    ? onboarding.flatMap(q => q.selected?.map(s => s.text) || [])
    : [];

  const interests = rawInterests.filter(interest => {
    if (!interest) return false;
    const lower = interest.toLowerCase().trim();
    if (lower === 'другое' || lower === 'other') return false;
    if (lower.startsWith('option #')) return false;
    return true;
  });

  if (loadingProfile) {
    return (
      <div className="profile-page profile-page--loading">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="profile-page edit-profile-page">
      
      <div className="profile-page__canvas">
<div className="profile-layout edit-profile-layout">
          {/* Left Column Card */}
          <div className="profile-card edit-profile-card">
            
            {/* Avatar Frame */}
            <div className="edit-profile-card__avatar-wrap" onClick={() => setShowPhotoMenu(!showPhotoMenu)}>
              <div className="profile-card__avatar-wrap edit-profile-card__avatar-frame">
                <img 
                  src={avatarUrl} 
                  alt="Avatar" 
                  className="profile-card__avatar"
                  onError={withAvatarFallback()} 
                />
                <div className="edit-profile-avatar-overlay">
                  {isUploading ? (
                    <svg className="progress-ring" viewBox="0 0 30 30" style={{ width: '50%', height: '50%' }}>
                      <circle
                        className="progress-ring__circle"
                        stroke="white"
                        strokeWidth="3"
                        fill="transparent"
                        r="12"
                        cx="15"
                        cy="15"
                      />
                    </svg>
                  ) : (
                    <svg className="edit-profile-avatar-overlay__icon" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M34.375 18.75C34.375 21.2364 33.3873 23.621 31.6291 25.3791C29.871 27.1373 27.4864 28.125 25 28.125C22.5136 28.125 20.129 27.1373 18.3709 25.3791C16.6127 23.621 15.625 21.2364 15.625 18.75C15.625 16.2636 16.6127 13.879 18.3709 12.1209C20.129 10.3627 22.5136 9.375 25 9.375C27.4864 9.375 29.871 10.3627 31.6291 12.1209C33.3873 13.879 34.375 16.2636 34.375 18.75Z" fill="var(--edit-profile-avatar-icon)"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M0 25C0 18.3696 2.63392 12.0107 7.32233 7.32233C12.0107 2.63392 18.3696 0 25 0C31.6304 0 37.9893 2.63392 42.6777 7.32233C47.3661 12.0107 50 18.3696 50 25C50 31.6304 47.3661 37.9893 42.6777 42.6777C37.9893 47.3661 31.6304 50 25 50C18.3696 50 12.0107 47.3661 7.32233 42.6777C2.63392 37.9893 0 31.6304 0 25ZM25 3.125C20.8806 3.12522 16.8449 4.28863 13.3575 6.48134C9.87012 8.67404 7.07276 11.8069 5.28739 15.5194C3.50202 19.2318 2.80121 23.3729 3.26562 27.4661C3.73003 31.5593 5.34079 35.4382 7.9125 38.6562C10.1313 35.0812 15.0156 31.25 25 31.25C34.9844 31.25 39.8656 35.0781 42.0875 38.6562C44.6592 35.4382 46.27 31.5593 46.7344 27.4661C47.1988 23.3729 46.498 19.2318 44.7126 15.5194C42.9272 11.8069 40.1299 8.67404 36.6425 6.48134C33.1551 4.28863 29.1194 3.12522 25 3.125Z" fill="var(--edit-profile-avatar-icon)"/>
                    </svg>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Title block */}
            <div className="profile-card__name-block-mobile">
              <div className="edit-profile-name-row">
                <AutoWidthInput 
                  className="edit-profile-name-input" 
                  value={firstName} 
                  onChange={(e) => setFirstName(e.target.value)} 
                  placeholder="Имя"
                />
                <AutoWidthInput 
                  className="edit-profile-name-input" 
                  value={lastName} 
                  onChange={(e) => setLastName(e.target.value)} 
                  placeholder="Фамилия"
                />
              </div>
              <div className="edit-profile-username-row">
                <span className="edit-profile-label">@</span>
                <AutoWidthInput 
                  className="edit-profile-username-input" 
                  value={username.replace(/^@/, '')} 
                  onChange={(e) => setUsername(e.target.value)} 
                  placeholder="username"
                />
                <span className="edit-profile-asterisk">*</span>
              </div>
            </div>

            {/* Context Dropdown */}
            {showPhotoMenu && (
              <div className="edit-profile-photo-menu" onClick={(e) => e.stopPropagation()}>
                <button className="edit-profile-photo-menu__item" onClick={(e) => { e.stopPropagation(); setShowPhotoModal(true); setShowPhotoMenu(false); }}>
                  <i className="bi bi-card-image"></i>
                  <span>Открыть фото</span>
                </button>
                <div className="edit-profile-photo-menu__separator"></div>
                <button className="edit-profile-photo-menu__item" onClick={(e) => { e.stopPropagation(); handleFileSelect(); setShowPhotoMenu(false); }}>
                  <i className="bi bi-pencil"></i>
                  <span>Изменить фото</span>
                </button>
                <div className="edit-profile-photo-menu__separator"></div>
                <button className="edit-profile-photo-menu__item" onClick={(e) => { e.stopPropagation(); handleGalleryFileSelect(); setShowPhotoMenu(false); }}>
                  <i className="bi bi-camera"></i>
                  <span>Добавить фото</span>
                </button>
                <div className="edit-profile-photo-menu__separator"></div>
                <button className="edit-profile-photo-menu__item edit-profile-photo-menu__item--danger" onClick={(e) => { e.stopPropagation(); handleDeleteAvatar(); setShowPhotoMenu(false); }}>
                  <i className="bi bi-trash3"></i>
                  <span>Удалить фото</span>
                </button>
              </div>
            )}

            {/* Meta input fields */}
            <div className="profile-card__meta edit-profile-card__meta">
              <div>
                <span>Почта<span style={{ color: 'var(--edit-profile-required)' }}>*</span></span>
                <input 
                  className="edit-profile-card__input" 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <span>Дата рождения<span style={{ color: 'var(--edit-profile-required)' }}>*</span></span>
                <input 
                  className="edit-profile-card__input" 
                  type="text" 
                  value={birthDate} 
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length > 8) val = val.slice(0, 8);
                    let formatted = '';
                    if (val.length > 0) {
                      formatted += val.slice(0, 2);
                      if (val.length > 2) {
                        formatted += '.' + val.slice(2, 4);
                        if (val.length > 4) {
                          formatted += '.' + val.slice(4, 8);
                        }
                      }
                    }
                    setBirthDate(formatted);
                  }} 
                  placeholder="ДД.ММ.ГГГГ"
                  style={{textAlign: 'right'}}
                />
              </div>
              <div>
                <span>Профессия<span style={{ color: 'var(--edit-profile-required)' }}>*</span></span>
                <input 
                  className="edit-profile-card__input" 
                  type="text" 
                  value={profession} 
                  onChange={(e) => setProfession(e.target.value)} 
                  placeholder="Ваша профессия"
                />
              </div>
              <div>
                <span>Город<span style={{ color: 'var(--edit-profile-required)' }}>*</span></span>
                <div style={{position: 'relative', width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
                  <input 
                    className="edit-profile-card__input" 
                    type="text" 
                    value={city} 
                    onChange={(e) => { setCity(e.target.value); setShowCitySuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                    placeholder="Город"
                    style={{textAlign: 'right'}}
                    spellCheck={false}
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="city-suggestions-dropdown">
                      {citySuggestions.map((s, i) => (
                        <div 
                          key={i} 
                          onClick={() => setCity(s)}
                          className="suggestion-item"
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <input type="file" ref={fileInputRef} style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }} accept="image/*" onChange={handleFileChange} />
            <input type="file" ref={galleryInputRef} style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }} accept="image/*" multiple onChange={handleGalleryFileChange} />
          </div>

          {/* Right Column Details */}
          <div className="profile-details edit-profile-details">
            {/* Desktop Name Row */}
            <div className="edit-profile-desktop-name-block">
              <div className="edit-profile-name-row">
                <AutoWidthInput 
                  className="edit-profile-name-input" 
                  value={firstName} 
                  onChange={(e) => setFirstName(e.target.value)} 
                  placeholder="Имя"
                />
                <AutoWidthInput 
                  className="edit-profile-name-input" 
                  value={lastName} 
                  onChange={(e) => setLastName(e.target.value)} 
                  placeholder="Фамилия"
                />
              </div>
              <div className="edit-profile-username-row">
                <span className="edit-profile-label">@</span>
                <AutoWidthInput 
                  className="edit-profile-username-input" 
                  value={username.replace(/^@/, '')} 
                  onChange={(e) => setUsername(e.target.value)} 
                  placeholder="username"
                />
                <span className="edit-profile-asterisk">*</span>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3>Описание<span style={{ color: 'var(--edit-profile-required)' }}>*</span></h3>
              <div 
                className={`edit-profile-description-box ${isBioOverflowing ? 'is-overflowing' : ''} ${isBioExpanded ? 'is-expanded' : ''}`}
                onClick={() => { if (bioRef.current) bioRef.current.focus(); }}
              >
                <textarea 
                  ref={bioRef}
                  className={`edit-profile-textarea ${isBioExpanded ? 'is-expanded' : ''}`} 
                  value={bio} 
                  onChange={(e) => e.target.value.length <= 1000 && setBio(e.target.value)} 
                  onFocus={() => setIsBioExpanded(true)}
                  onBlur={() => setIsBioExpanded(false)}
                  placeholder="Люблю кислые мармеладки и спать)))"
                />
                <span className="edit-profile-textarea__counter">Не более 1000 символов</span>
              </div>
            </div>

            {/* Questionnaire Link */}
            <Link to="/new-questionnaire" className="profile-page__back edit-profile-questionnaire-link">
              <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.4588 0.20197C17.5256 0.137948 17.6049 0.0871543 17.6922 0.0524969C17.7795 0.0178396 17.8731 0 17.9677 0C18.0622 0 18.1558 0.0178396 18.2431 0.0524969C18.3305 0.0871543 18.4098 0.137948 18.4765 0.20197L22.7888 4.32679C22.8558 4.39065 22.9089 4.46651 22.9451 4.55003C22.9813 4.63355 23 4.72309 23 4.81352C23 4.90394 22.9813 4.99348 22.9451 5.077C22.9089 5.16052 22.8558 5.23639 22.7888 5.30025L8.41448 19.0496C8.3455 19.1152 8.26335 19.1666 8.17299 19.2009L0.985803 21.9508C0.855192 22.0008 0.712109 22.013 0.574293 21.986C0.436477 21.9589 0.309988 21.8938 0.210508 21.7986C0.111028 21.7035 0.0429317 21.5825 0.0146609 21.4507C-0.0136098 21.3189 -0.000811566 21.182 0.051469 21.0571L2.92634 14.1824C2.96218 14.0959 3.01597 14.0173 3.08446 13.9514L17.4588 0.20197ZM16.1091 3.43858L19.4051 6.59132L21.2637 4.81352L17.9677 1.66078L16.1091 3.43858ZM18.3889 7.5634L15.0928 4.41066L5.74947 13.3478V13.7506H6.46819C6.6588 13.7506 6.84161 13.8231 6.9764 13.952C7.11118 14.0809 7.18691 14.2558 7.18691 14.4381V15.1256H7.90562C8.09624 15.1256 8.27905 15.198 8.41384 15.3269C8.54862 15.4558 8.62434 15.6307 8.62434 15.813V16.5005H9.04551L18.3889 7.5634ZM4.35803 14.6787L4.20566 14.8245L2.00926 20.0781L7.50171 17.9772L7.65407 17.8314C7.51697 17.7824 7.39877 17.6946 7.31529 17.5796C7.23181 17.4646 7.18702 17.328 7.18691 17.188V16.5005H6.46819C6.27757 16.5005 6.09476 16.4281 5.95998 16.2992C5.82519 16.1702 5.74947 15.9954 5.74947 15.813V15.1256H5.03075C4.88439 15.1255 4.74155 15.0826 4.62134 15.0028C4.50112 14.9229 4.40926 14.8099 4.35803 14.6787Z" fill="var(--edit-profile-questionnaire-icon)"/>
              </svg>
              <span>Редактировать анкету</span>
            </Link>

            {/* Tags (interests) */}
            {interests.length > 0 && (
              <ProfileTagsCollapse tags={interests} className="profile-tags edit-profile-tags" />
            )}

            {/* Photos */}
            <div className="edit-profile-photos-section">
              <div className="edit-profile-photos-section__header">
                <h2>Фотографии</h2>
                <button className="edit-profile-upload-photo" onClick={handleGalleryFileSelect}>
                  Загрузить фото
                </button>
              </div>
              
              <div className={`profile-photos edit-profile-photos ${isExpanded ? 'edit-profile-photos--expanded' : ''}`} ref={containerRef}>
                {displayPhotos.map((photo, idx) => {
                  const isHidden = !isExpanded && idx >= visibleCount;
                  const isMore = !isExpanded && idx === visibleCount - 1 && remainingCount > 0;
                  
                  return (
                    <div 
                      key={photo.id || `edit-ph-${idx}`} 
                      className={`profile-photos__item ${photo.isPlaceholder ? 'profile-photos__item--empty' : ''} ${isMore ? 'profile-photos__item--more' : ''} ${isHidden ? 'profile-photos__item--hidden' : ''}`}
                      onClick={() => {
                        if (isMore) {
                          setIsExpanded(true);
                          return;
                        }
                        if (photo.isPlaceholder) return;
                        setAvatarUrl(photo.url);
                        setCurrentPhotoId(photo.id);
                      }}
                      style={{
                        cursor: (photo.isPlaceholder && !isMore) ? 'default' : 'pointer', 
                        position: 'relative',
                        display: isHidden ? 'none' : 'flex'
                      }}
                    >
                      {photo.url ? (
                        <>
                          <img src={photo.url} alt="" className="w-full h-full object-cover" />
                          {isMore && (
                            <>
                              <div className="profile-photos__film"></div>
                              <div className="profile-photos__more">+{remainingCount}</div>
                            </>
                          )}
                          {!isMore && (
                            <button 
                              className="edit-profile-photo-delete" 
                              onClick={(e) => handleDeleteSpecificPhoto(photo.id, e)}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="profile-photos__empty-slot" style={{ background: 'var(--edit-profile-photo-empty-bg)', width: '100%', height: '100%', position: 'relative' }}>
                          {isMore && (
                            <>
                              <div className="profile-photos__film"></div>
                              <div className="profile-photos__more" style={{zIndex: 20}}>+{remainingCount}</div>
                            </>
                          )}
                        </div>
                      )}
                      {photo.id === currentPhotoId && !photo.isPlaceholder && !isMore && (
                        <div className="edit-profile-photo-selected"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button className="edit-profile-save-button" onClick={handleSave}>
          Сохранить
        </button>
      </div>

      {showPhotoModal && (
        <div className="edit-profile-photo-modal" onClick={() => setShowPhotoModal(false)}>
          <div className="edit-profile-photo-modal__content" onClick={e => e.stopPropagation()}>
            <img src={avatarUrl} alt="Full size avatar" className="edit-profile-photo-modal__image" />
            <button 
              className="edit-profile-photo-modal__close"
              onClick={() => setShowPhotoModal(false)}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default EditProfile;
